import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, QrCode, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const QR_COLORS = [
  "#0F8F4F",
  "#0EA5E9",
  "#00C16E",
  "#09090B",
  "#EF4444",
  "#FACC15",
  "#A855F7",
  "#F97316",
  "#14B8A6",
];

const QR_STYLES = ["square", "rounded", "dots"];

export default function Onboarding() {
  const { refreshProperties, setActiveProperty, user, properties } = useAuth();
  const nav = useNavigate();

  // Once the user has submitted the wizard we OWN the navigation — block the
  // redirect-if-already-onboarded effect from racing the post-create redirect
  // to /dashboard/billing.
  const justSubmittedRef = useRef(false);

  // Only auto-redirect on INITIAL mount if the user landed here by mistake
  // (they already have a property AND didn't click "+ Add new property").
  // The Layout's "+ Add new property" menu item passes ?add=1 so power users
  // can create a 2nd/3rd property.
  const wantsAdditional = new URLSearchParams(window.location.search).get(
    "add"
  ) === "1";
  const initialCheckRef = useRef(true);
  useEffect(() => {
    if (justSubmittedRef.current) return;
    if (!initialCheckRef.current) return;
    if (wantsAdditional) {
      initialCheckRef.current = false;
      return;
    }
    if (properties && properties.length > 0) {
      initialCheckRef.current = false;
      nav("/dashboard", { replace: true });
    } else if (properties !== null) {
      // properties loaded, none exist — legit new-user onboarding
      initialCheckRef.current = false;
    }
  }, [properties, nav, wantsAdditional]);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    corporation_name: "",
    property_name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    manager_name: user?.name || "",
    manager_phone: "",
    manager_email: user?.email || "",
    // Billing entity (required for invoice generation)
    legal_company_name: "",
    billing_address: "",
    billing_city: "",
    billing_state: "",
    billing_zip: "",
    billing_contact_name: user?.name || "",
    billing_contact_email: user?.email || "",
    billing_contact_phone: "",
    billing_mode: "invoice",
    qr_color: "#0F8F4F",
    qr_style: "square",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // "Same as property address" convenience for the billing entity step.
  const copyPropertyAddress = () => {
    setForm((f) => ({
      ...f,
      billing_address: f.address,
      billing_city: f.city,
      billing_state: f.state,
      billing_zip: f.zip,
    }));
    toast.success("Property address copied");
  };

  const submit = async () => {
    setLoading(true);
    justSubmittedRef.current = true;
    try {
      const { data } = await api.post("/properties", form);
      toast.success("Account created — pay your setup fee to start adding tenants.");
      const props = await refreshProperties();
      const created = props.find((p) => p.id === data.id);
      if (created) setActiveProperty(created);
      // Setup-fee gate: every new property must settle the $99 setup
      // fee before tenant/vehicle creation unlocks. This screen offers
      // both the 1-click Stripe payment and the "Receive Invoice" path.
      nav("/onboarding/setup-fee", { replace: true });
    } catch (err) {
      justSubmittedRef.current = false;
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { title: "Corporation", desc: "Tell us who owns the property" },
    { title: "Location", desc: "Where is the property?" },
    { title: "Contact", desc: "Who manages this property?" },
    { title: "Billing", desc: "Where should we send the invoice?" },
    { title: "Branding", desc: "QR sticker look & feel" },
    { title: "Pricing", desc: "Review your billing model" },
  ];

  const canNext = () => {
    if (step === 0) return form.corporation_name && form.property_name;
    if (step === 1) return form.address && form.city && form.state && form.zip;
    if (step === 2) return form.manager_name && form.manager_phone && form.manager_email;
    if (step === 3) {
      return (
        form.legal_company_name &&
        form.billing_address &&
        form.billing_city &&
        form.billing_state &&
        form.billing_zip &&
        form.billing_contact_name &&
        form.billing_contact_email &&
        form.billing_contact_phone
      );
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] px-4 py-8 md:py-16">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-[#0F8F4F] flex items-center justify-center">
            <QrCode className="w-4 h-4 text-white" />
          </div>
          <span className="font-display text-lg font-bold">ScanMint</span>
          <span className="mono-tiny text-[#71717A] ml-auto" data-testid="onboarding-step-indicator">
            Step {step + 1} of {steps.length}
          </span>
        </div>

        {/* Step pills */}
        <div className="grid grid-cols-6 gap-2 mb-8">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className={`h-1.5 rounded-full ${
                i <= step ? "bg-[#0F8F4F]" : "bg-[#E4E4E7]"
              }`}
            />
          ))}
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-10">
          <div className="mono-tiny text-[#71717A]">{steps[step].desc}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            {steps[step].title}
          </h1>

          <div className="mt-8 space-y-4">
            {step === 0 && (
              <>
                <Field label="Corporation name" id="corp">
                  <Input
                    id="corp"
                    value={form.corporation_name}
                    onChange={(e) => update("corporation_name", e.target.value)}
                    placeholder="e.g. Mint Capital Holdings"
                    className="h-12"
                    data-testid="onb-corp"
                  />
                </Field>
                <Field label="Property name" id="prop">
                  <Input
                    id="prop"
                    value={form.property_name}
                    onChange={(e) => update("property_name", e.target.value)}
                    placeholder="e.g. Parkview Residences"
                    className="h-12"
                    data-testid="onb-property"
                  />
                </Field>
              </>
            )}

            {step === 1 && (
              <>
                <Field label="Street address" id="addr">
                  <Input
                    id="addr"
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    className="h-12"
                    data-testid="onb-address"
                  />
                </Field>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="City" id="city">
                    <Input
                      id="city"
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      className="h-12"
                      data-testid="onb-city"
                    />
                  </Field>
                  <Field label="State" id="state">
                    <Input
                      id="state"
                      value={form.state}
                      onChange={(e) => update("state", e.target.value)}
                      className="h-12"
                      data-testid="onb-state"
                    />
                  </Field>
                  <Field label="ZIP" id="zip">
                    <Input
                      id="zip"
                      value={form.zip}
                      onChange={(e) => update("zip", e.target.value)}
                      className="h-12"
                      data-testid="onb-zip"
                    />
                  </Field>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <Field label="Manager name" id="mname">
                  <Input
                    id="mname"
                    value={form.manager_name}
                    onChange={(e) => update("manager_name", e.target.value)}
                    className="h-12"
                    data-testid="onb-manager-name"
                  />
                </Field>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Manager phone" id="mphone">
                    <Input
                      id="mphone"
                      value={form.manager_phone}
                      onChange={(e) => update("manager_phone", e.target.value)}
                      className="h-12"
                      data-testid="onb-manager-phone"
                    />
                  </Field>
                  <Field label="Manager email" id="memail">
                    <Input
                      id="memail"
                      type="email"
                      value={form.manager_email}
                      onChange={(e) => update("manager_email", e.target.value)}
                      className="h-12"
                      data-testid="onb-manager-email"
                    />
                  </Field>
                </div>
              </>
            )}

            {step === 3 && (
              <div data-testid="onb-billing-step" className="space-y-4">
                <div className="border border-[#0F8F4F]/30 bg-[#0F8F4F]/5 rounded-md p-4 text-sm text-[#3F3F46]">
                  <b>Why we ask:</b> these details appear on your PDF invoice
                  and are required so we can bill the right entity. You can
                  edit them any time from Settings.
                </div>
                <Field label="Legal company name" id="legal_co">
                  <Input
                    id="legal_co"
                    value={form.legal_company_name}
                    onChange={(e) => update("legal_company_name", e.target.value)}
                    placeholder="e.g. Mint Capital Holdings, LLC"
                    className="h-12"
                    data-testid="onb-legal-company"
                  />
                </Field>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="mono-tiny text-[#71717A]">Billing address</Label>
                  <button
                    type="button"
                    onClick={copyPropertyAddress}
                    className="text-xs text-[#0F8F4F] hover:underline"
                    data-testid="onb-copy-property-address"
                  >
                    Same as property address
                  </button>
                </div>
                <Field label="Street" id="bill_addr">
                  <Input
                    id="bill_addr"
                    value={form.billing_address}
                    onChange={(e) => update("billing_address", e.target.value)}
                    className="h-12"
                    data-testid="onb-billing-address"
                  />
                </Field>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="City" id="bill_city">
                    <Input
                      id="bill_city"
                      value={form.billing_city}
                      onChange={(e) => update("billing_city", e.target.value)}
                      className="h-12"
                      data-testid="onb-billing-city"
                    />
                  </Field>
                  <Field label="State" id="bill_state">
                    <Input
                      id="bill_state"
                      value={form.billing_state}
                      onChange={(e) => update("billing_state", e.target.value)}
                      className="h-12"
                      data-testid="onb-billing-state"
                    />
                  </Field>
                  <Field label="ZIP" id="bill_zip">
                    <Input
                      id="bill_zip"
                      value={form.billing_zip}
                      onChange={(e) => update("billing_zip", e.target.value)}
                      className="h-12"
                      data-testid="onb-billing-zip"
                    />
                  </Field>
                </div>
                <Field label="Billing contact name" id="bill_contact">
                  <Input
                    id="bill_contact"
                    value={form.billing_contact_name}
                    onChange={(e) => update("billing_contact_name", e.target.value)}
                    className="h-12"
                    data-testid="onb-billing-contact-name"
                  />
                </Field>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Billing email" id="bill_email">
                    <Input
                      id="bill_email"
                      type="email"
                      value={form.billing_contact_email}
                      onChange={(e) => update("billing_contact_email", e.target.value)}
                      className="h-12"
                      data-testid="onb-billing-contact-email"
                    />
                  </Field>
                  <Field label="Billing phone" id="bill_phone">
                    <Input
                      id="bill_phone"
                      value={form.billing_contact_phone}
                      onChange={(e) => update("billing_contact_phone", e.target.value)}
                      className="h-12"
                      data-testid="onb-billing-contact-phone"
                    />
                  </Field>
                </div>
              </div>
            )}

            {step === 4 && (
              <>
                <Field label="QR color">
                  <div className="flex flex-wrap gap-3">
                    {QR_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => update("qr_color", c)}
                        data-testid={`onb-color-${c.replace("#", "")}`}
                        className={`w-12 h-12 rounded-md border-2 transition-all ${
                          form.qr_color === c
                            ? "border-[#09090B] scale-110"
                            : "border-[#E4E4E7]"
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </Field>
                <Field label="QR style">
                  <div className="grid grid-cols-3 gap-3">
                    {QR_STYLES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => update("qr_style", s)}
                        data-testid={`onb-style-${s}`}
                        className={`h-14 rounded-md border-2 font-medium capitalize transition-all ${
                          form.qr_style === s
                            ? "border-[#0F8F4F] bg-[#0F8F4F]/5 text-[#0F8F4F]"
                            : "border-[#E4E4E7] text-[#09090B]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </Field>
                <div className="border border-[#E4E4E7] rounded-md p-4 bg-[#FAFAFA] flex items-center gap-4">
                  <Palette className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
                  <div className="text-sm text-[#71717A]">
                    Every QR sticker you generate uses this color & style.
                  </div>
                </div>
              </>
            )}

            {step === 5 && (
              <div className="space-y-4" data-testid="onb-pricing-step">
                <div className="border-2 border-[#0F8F4F]/30 bg-[#0F8F4F]/5 rounded-lg p-5">
                  <div className="mono-tiny text-[#0F8F4F]">Default · Invoice billing</div>
                  <div className="font-display text-2xl font-bold mt-1">Invoice billing</div>
                  <div className="text-sm text-[#3F3F46] mt-2">
                    After this step, add tenants &amp; vehicles and click{" "}
                    <b>Start Your Service</b> on the Review page. We'll
                    generate a single PDF invoice covering the $99 setup
                    fee + $1.00 per active car per month and email it
                    to your billing contact. QR codes auto-activate when
                    the invoice is paid. <b className="text-[#0F8F4F]">
                    First 30 days after setup are free.</b>
                  </div>
                </div>
                <div className="border-2 border-[#FACC15]/40 bg-[#FFFBEA] rounded-lg p-5">
                  <div className="mono-tiny text-[#92400E]">One-time fee</div>
                  <div className="font-display text-2xl font-bold mt-1">$99 setup</div>
                  <ul className="text-sm text-[#3F3F46] mt-3 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-[#0F8F4F]" /> Inkless mini printer
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-[#0F8F4F]" /> QR code sticker supply
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-[#0F8F4F]" /> Welcome packet
                    </li>
                  </ul>
                </div>
                <div className="border-2 border-[#0F8F4F]/30 bg-[#0F8F4F]/5 rounded-lg p-5">
                  <div className="mono-tiny text-[#0F8F4F]">Monthly billing</div>
                  <div className="font-display text-2xl font-bold mt-1">
                    $1.00 / car / month
                  </div>
                  <div className="text-sm text-[#3F3F46] mt-2">
                    Every active car is billed. Volume discounts:
                    <b> 200–499 cars $0.90</b>, <b>500–999 $0.85</b>,
                    <b> 1000+ requires a quote</b>.
                  </div>
                  <div className="mono-tiny text-[#0F8F4F]/80 mt-3">
                    Example: 50 active cars = $50 / mo (after free month)
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                data-testid="onb-back"
                className="h-12"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            ) : (
              <div />
            )}
            {step < steps.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
                data-testid="onb-next"
                className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={loading}
                data-testid="onb-finish"
                className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              >
                {loading ? "Setting up…" : "Set up your account"} <Check className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="mono-tiny text-[#71717A]">
        {label}
      </Label>
      {children}
    </div>
  );
}
